import { createRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Modal, useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Workflow } from 'components/Handbook2_0/HandbookSettings/Workflow/Workflow'
import { BaseSectionProps, SettingsTab } from 'components/Handbook2_0/HandbookSettings/Types'
import { useDocumentationFeatures } from 'hooks/useDocumentationFeatures'
import { Documentation } from 'components/Handbook2_0/HandbookSettings/Documentation'
import { isLegacyHandbook } from 'components/Handbook2_0/util'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { CustomUrl } from 'components/Handbook2_0/HandbookSettings/CustomUrl'
import { DeleteHandbook } from 'components/Handbook2_0/HandbookSettings/DeleteHandbook'
import Styles from './FolderSettingsModal.module.scss'
import { SettingsSection } from 'components/Handbook2_0/HandbookSettings/SettingsSection'
import { WorkflowBuilderHandle } from 'components/Handbook2_0/HandbookSettings/Workflow/UpdateForm/Types'
import { useHandbookUpdate } from 'components/Handbook2_0/HandbookSettings/hooks/useHandbookUpdate'
import { Project } from 'api/clients/ProjectClient'
import { defer, invert, mapValues } from 'lodash'

export const useAnchorRefs = (initial: SettingsTab | undefined, setActiveTab: (tab: SettingsTab) => void) => {
  const anchorRefs = mapValues(invert(SettingsTab), () => createRef<HTMLDivElement>())

  const handleNavItemClick = (tab: SettingsTab) => {
    // using defer so we wait react render the full settings page and be able to scroll to the initial position
    defer(() => {
      anchorRefs[tab].current?.scrollIntoView()
      setActiveTab(tab)
    })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => initial && handleNavItemClick(initial), [])

  return [anchorRefs, handleNavItemClick] as [typeof anchorRefs, typeof handleNavItemClick]
}

export const SETTINGS_COMPONENTS: {
  [key: string]: React.ComponentClass<any, any> | ((...args: any) => JSX.Element | null)
} = {
  statuses: Workflow,
  documentation: Documentation,
  'custom url': CustomUrl,
  delete: DeleteHandbook,
}

interface Props {
  handbookKey: string
  folderTitle: string
}

interface SettingsProps extends BaseSectionProps {
  handbookKey: string
  folder: Project
  ref: React.RefObject<WorkflowBuilderHandle>
  onClose: () => void
}

export const FolderSettingsModal = observer(({ handbookKey, folderTitle }: Props) => {
  const scrollParentRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const history = useHistory()
  const { isMobile } = useResponsiveDesign()
  const { openModal, closeModal } = useModals()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location])
  const { isDocumentationEnabled } = useDocumentationFeatures()
  const store = useRootStore().handbooks
  const handbook = store.get(handbookKey)?.handbook
  const folder = handbook?.folder
  const isLegacy = handbook && isLegacyHandbook(handbook)
  const [activeTab, setActiveTab] = useState<SettingsTab>(SettingsTab.Documentation)
  const [anchorRefs, handleNavItemClick] = useAnchorRefs(SettingsTab.Workflow, setActiveTab)
  const workflowBuilder = useRef<WorkflowBuilderHandle>(null)
  const { onUpdate, isUpdating } = useHandbookUpdate(store, handbook)

  const SETTINGS_ITEMS = [
    {
      key: SettingsTab.Documentation,
      label: 'Documentation',
      enabled: isDocumentationEnabled,
      component: (props: SettingsProps) => <Documentation {...props} />,
    },
    {
      key: SettingsTab.Workflow,
      label: 'Statuses & Automations',
      enabled: !isLegacy && handbook?.folder,
      component: (props: SettingsProps) => <Workflow {...props} />,
    },
    {
      key: SettingsTab.CustomUrl,
      label: 'Custom URL',
      enabled: isDocumentationEnabled,
      component: (props: SettingsProps) => <CustomUrl {...props} />,
    },
    {
      key: SettingsTab.Delete,
      label: 'Delete',
      enabled: !!handbook,
      component: (props: SettingsProps) => <DeleteHandbook {...props} />,
    },
  ]

  const openMobileModal = (key: string) => {
    openModal(
      FolderSettingsPageModal,
      {
        selectedItem: key,
        title: SETTINGS_ITEMS.find((item) => item.key === key)?.label || '',
        onClose: handleClose,
        handbookKey,
        workflowBuilder,
      },
      { newStack: true },
    )
  }

  // Open settings page directly if one is passed as a query param
  useEffect(() => {
    const settingsView = queryParams.get('option')
    queryParams.delete('option')
    history.push({ search: queryParams.toString() })

    if (settingsView && isMobile) openMobileModal(settingsView)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = async () => {
    if (activeTab !== SettingsTab.Workflow || !workflowBuilder.current) {
      closeModal()
      return
    }

    const confirm = await workflowBuilder.current.confirmUnsavedChanges()
    if (confirm) {
      closeModal()
    }
  }

  if (!folder || !handbook) return null

  if (isMobile)
    return (
      <Modal.Root size="sm">
        <Modal.Header title={`${folderTitle} Settings`} />
        <Modal.Content>
          <Modal.ListSection title="Settings">
            {SETTINGS_ITEMS.filter((item) => item.enabled).map((item) => (
              <Modal.ListItem itemLabel={item.label} key={item.key} itemKey={item.key} onClick={openMobileModal} />
            ))}
          </Modal.ListSection>
        </Modal.Content>
      </Modal.Root>
    )

  return (
    <Modal.Root size="lg" onPressOutside={handleClose}>
      <Modal.Header title={`${folderTitle} Settings`} />
      <div className={Styles.content}>
        <div className={Styles.navigation}>
          {SETTINGS_ITEMS.filter((item) => item.enabled).map((item) => (
            <Modal.ListItem
              itemLabel={item.label}
              key={item.key}
              itemKey={item.key}
              onClick={() => handleNavItemClick(item.key)}
              isSelected={activeTab === item.key}
            />
          ))}
        </div>
        <div className={Styles.sections} ref={scrollParentRef}>
          {SETTINGS_ITEMS.filter((item) => item.enabled).map((item) => (
            <>
              <SettingsSection
                tab={item.key}
                parentRef={scrollParentRef}
                onIntersect={() => setActiveTab(item.key)}
                isFirst
              >
                <div ref={anchorRefs[item.key]} />
                {item.key !== SettingsTab.CustomUrl && <h1 className={Styles.sectionTitle}>{item.label}</h1>}
                {item.component({
                  handbookKey: handbook.key,
                  folder: folder,
                  ref: workflowBuilder,
                  onClose: handleClose,
                  handbook: handbook,
                  onUpdate: onUpdate,
                  isUpdating: isUpdating,
                })}
              </SettingsSection>
              <div className={Styles.divider} />
            </>
          ))}
        </div>
      </div>
    </Modal.Root>
  )
})

interface FolderSettingsPageModalProps {
  selectedItem: string
  handbookKey: string
  title: string
  onClose: () => void
  workflowBuilder: React.RefObject<WorkflowBuilderHandle>
}

const FolderSettingsPageModal = observer(
  ({ selectedItem, handbookKey, title, onClose, workflowBuilder }: FolderSettingsPageModalProps) => {
    const store = useRootStore().handbooks
    const handbook = store.get(handbookKey)?.handbook
    const SettingsViewModal = SETTINGS_COMPONENTS[selectedItem] || Workflow
    const { onUpdate, isUpdating } = useHandbookUpdate(store, handbook)
    const { isMobile } = useResponsiveDesign()
    const { closeModal, stack } = useModals()

    useLayoutEffect(() => {
      if (!isMobile && stack.length > 1) closeModal()
    }, [isMobile, closeModal, stack])

    return (
      <Modal.Root size="md" onPressOutside={onClose}>
        {selectedItem !== SettingsTab.Workflow && <Modal.Header title={title} onClose={onClose} />}
        <SettingsViewModal
          handbookKey={handbook?.key}
          folder={handbook?.folder}
          ref={workflowBuilder}
          onClose={onClose}
          handbook={handbook}
          onUpdate={onUpdate}
          isUpdating={isUpdating}
        />
      </Modal.Root>
    )
  },
)
